import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import { ABGW_StorageType } from '@schema';

export const ABGW_STORAGE_TYPE_LOCAL = ABGW_StorageType.LOCAL;
export const ABGW_STORAGE_TYPE_NFS = ABGW_StorageType.NFS;
export const ABGW_STORAGE_TYPE_CLOUD = 'cloud';

export const SOFT_THRESHOLD = 'soft_threshold';
export const S3_THRESHOLD = 's3_threshold';

export const MIN_THROTTLING_LIMITS = {
  [SOFT_THRESHOLD]: 85,
  [S3_THRESHOLD]: 300,
};
export const MAX_THROTTLING_LIMITS = {
  [SOFT_THRESHOLD]: 95,
  [S3_THRESHOLD]: 100000,
};

const ABGW_CLOUDS_LIST = {
  ALIBABA: 'alibaba',
  S3: 's3',
  AUTHV2: 'authv2',
  AUTHV4: 'authv4',
  CLEVERSAFE: 'cleversafe',
  CLOUDIAN: 'cloudian',
  GOOGLE: 'google',
  IBM: 'ibm',
  IIJ: 'iij',
  AZURE: 'azure',
  SOFTLAYER: 'softlayer',
  SWIFT: 'swift',
  WASABI: 'wasabi',
};

export const ABC_ABGW_CLOUDS = [
  [ABGW_CLOUDS_LIST.ALIBABA],
  [ABGW_CLOUDS_LIST.S3],
  [ABGW_CLOUDS_LIST.AUTHV2],
  [ABGW_CLOUDS_LIST.AUTHV4],
  [ABGW_CLOUDS_LIST.CLEVERSAFE],
  [ABGW_CLOUDS_LIST.CLOUDIAN],
  [ABGW_CLOUDS_LIST.GOOGLE],
  [ABGW_CLOUDS_LIST.IBM],
  [ABGW_CLOUDS_LIST.IIJ],
  [ABGW_CLOUDS_LIST.AZURE],
  [ABGW_CLOUDS_LIST.SOFTLAYER],
  [ABGW_CLOUDS_LIST.SWIFT],
  [ABGW_CLOUDS_LIST.WASABI]
];

export const ABGW_CLOUDS = computedLocale(() => ({
  [ABGW_CLOUDS_LIST.ALIBABA]: {
    key: ABGW_CLOUDS_LIST.ALIBABA,
    label: i18n.t('Alibaba Cloud (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'alibaba', cert_verify: true }
  },
  [ABGW_CLOUDS_LIST.S3]: {
    key: ABGW_CLOUDS_LIST.S3,
    label: i18n.t('Amazon S3'),
    storage_type: 's3',
    storage_params: { flavour: 'amazon', cert_verify: true },
  },
  [ABGW_CLOUDS_LIST.AUTHV2]: {
    key: ABGW_CLOUDS_LIST.AUTHV2,
    label: i18n.t('AuthV2 compatible (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'authv2', cert_verify: true }
  },
  [ABGW_CLOUDS_LIST.AUTHV4]: {
    key: ABGW_CLOUDS_LIST.AUTHV4,
    label: i18n.t('AuthV4 compatible (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'authv4', cert_verify: true }
  },
  [ABGW_CLOUDS_LIST.CLEVERSAFE]: {
    key: ABGW_CLOUDS_LIST.CLEVERSAFE,
    label: i18n.t('Cleversafe (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'authv2', cert_verify: true },
  },
  [ABGW_CLOUDS_LIST.CLOUDIAN]: {
    key: ABGW_CLOUDS_LIST.CLOUDIAN,
    label: i18n.t('Cloudian (S3)'),
    storage_type: 's3',
    params: { flavour: 'authv2', cert_verify: true }
  },
  [ABGW_CLOUDS_LIST.GOOGLE]: {
    key: ABGW_CLOUDS_LIST.GOOGLE,
    label: i18n.t('Google Cloud'),
    storage_type: 'google',
    storage_params: { }
  },
  [ABGW_CLOUDS_LIST.IBM]: {
    key: ABGW_CLOUDS_LIST.IBM,
    label: i18n.t('IBM Cloud (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'authv2', cert_verify: true }
  },
  [ABGW_CLOUDS_LIST.IIJ]: {
    key: ABGW_CLOUDS_LIST.IIJ,
    label: i18n.t('IIJ (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'authv2', cert_verify: true }
  },
  [ABGW_CLOUDS_LIST.AZURE]: {
    key: ABGW_CLOUDS_LIST.AZURE,
    label: i18n.t('Microsoft Azure'),
    storage_type: 'azure',
    storage_params: { endpoint: 'core.windows.net' }
  },
  [ABGW_CLOUDS_LIST.SOFTLAYER]: {
    key: ABGW_CLOUDS_LIST.SOFTLAYER,
    label: i18n.t('Softlayer (Swift)'),
    storage_type: 'swift',
    storage_params: {}
  },
  [ABGW_CLOUDS_LIST.SWIFT]: {
    key: ABGW_CLOUDS_LIST.SWIFT,
    label: i18n.t('Swift object storage'),
    storage_type: 'swift',
    storage_params: { auth_version: '1' }
  },
  [ABGW_CLOUDS_LIST.WASABI]: {
    key: ABGW_CLOUDS_LIST.WASABI,
    label: i18n.t('Wasabi (S3)'),
    storage_type: 's3',
    storage_params: { flavour: 'authv2', endpoint: 'https://s3.wasabisys.com', cert_verify: true },
  }
}));

export const AMAZON_REGIONS = computedLocale(() => ([
  { label: i18n.t('US East (Ohio)'), value: 'us-east-2', group: 'us' },
  { label: i18n.t('US East (N. Virginia)'), value: 'us-east-1', group: 'us' },
  { label: i18n.t('US West (N. California)'), value: 'us-west-1', group: 'us' },
  { label: i18n.t('US West (Oregon)'), value: 'us-west-2', group: 'us' },
  { label: i18n.t('Africa (Cape Town)'), value: 'af-south-1', group: 'af' },
  { label: i18n.t('Asia Pacific (Hong Kong)'), value: 'ap-east-1', group: 'ap' },
  { label: i18n.t('Asia Pacific (Mumbai)'), value: 'ap-south-1', group: 'ap' },
  { label: i18n.t('Asia Pacific (Osaka-Local)'), value: 'ap-northeast-3', group: 'ap' },
  { label: i18n.t('Asia Pacific (Seoul)'), value: 'ap-northeast-2', group: 'ap' },
  { label: i18n.t('Asia Pacific (Singapore)'), value: 'ap-southeast-1', group: 'ap' },
  { label: i18n.t('Asia Pacific (Sydney)'), value: 'ap-southeast-2', group: 'ap' },
  { label: i18n.t('Asia Pacific (Tokyo)'), value: 'ap-northeast-1', group: 'ap' },
  { label: i18n.t('Canada (Central)'), value: 'ca-central-1', group: 'ca' },
  { label: i18n.t('China (Beijing)'), value: 'cn-north-1', group: 'us' },
  { label: i18n.t('China (Ningxia)'), value: 'cn-northwest-1', group: 'us' },
  { label: i18n.t('Europe (Frankfurt)'), value: 'eu-central-1', group: 'eu' },
  { label: i18n.t('Europe (Ireland)'), value: 'eu-west-1', group: 'eu' },
  { label: i18n.t('Europe (London)'), value: 'eu-west-2', group: 'eu' },
  { label: i18n.t('Europe (Milan)'), value: 'eu-south-1', group: 'eu' },
  { label: i18n.t('Europe (Paris)'), value: 'eu-west-3', group: 'eu' },
  { label: i18n.t('Europe (Stockholm)'), value: 'eu-north-1', group: 'eu' },
  { label: i18n.t('Middle East (Bahrain)'), value: 'me-south-1', group: 'me' },
  { label: i18n.t('South America (São Paulo)'), value: 'sa-east-1', group: 'us' },
]));

export const ABGW_STATUSES = {
  DEPLOYING: 'deploying',
  DEPLOYED: 'deployed',
  DEPLOYING_FAILED: 'deploying-failed',
  REGISTERING_FAILED: 'registering-failed',
  UN_REGISTERING_FAILED: 'un-registering-failed',
  MODIFYING_FAILED: 'modifying-failed',
  LEAVING_FAILED: 'leaving-failed',
  LEAVING: 'leaving',
};

export const ABGW_DEPLOYMENT_MODES = {
  REVERSE_PROXY: 'reverse-proxy',
  STANDALONE: 'standalone',
  UPSTREAM: 'upstream',
  STANDALONE_AND_UPSTREAM: 'standalone+upstream'
};

export const ABGW_DEPLOYMENT_MODES_LABELS = computedLocale(() => ({
  [ABGW_DEPLOYMENT_MODES.REVERSE_PROXY]: i18n.t('REVERSE PROXY'),
  [ABGW_DEPLOYMENT_MODES.STANDALONE_AND_UPSTREAM]: i18n.t('STANDALONE + UPSTREAM'),
  [ABGW_DEPLOYMENT_MODES.UPSTREAM]: i18n.t('UPSTREAM')
}));

export const ABGW_REGISTRATION_EVENTS = {
  ADD: 'abgw-registration-add',
  DELETE: 'abgw-registration-delete',
  UPDATE: 'abgw-registration-update',
};
